import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { StatCard } from '../../components/ui/StatCard';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Users, MapPin, CheckCircle, Calendar, Heart, Globe, Instagram, Facebook, Mail, Phone, Image as ImageIcon } from 'lucide-react';
export function KomunitasDetailPage() {
  const {
    id
  } = useParams();
  const [activeTab, setActiveTab] = useState<'profil' | 'kegiatan' | 'galeri' | 'kontak'>('profil');
  // Mock Data
  const communityData = {
    name: 'Komunitas Nol Sampah Surabaya',
    location: 'Surabaya, Jawa Timur',
    verified: true,
    members: 1250,
    established: 2018,
    focus: ['Edukasi', 'Zero Waste', 'Advokasi'],
    description: 'Gerakan masyarakat sipil yang berfokus pada pengurangan sampah dari sumber melalui edukasi, kampanye, dan pendampingan warga.',
    stats: {
      actions: 45,
      participants: '5.2K',
      tonnage: '12.5'
    }
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col font-sans" data-id="element-4355">
      <Header data-id="element-4356" />

      <main className="flex-1" data-id="element-4357">
        {/* Profile Header */}
        <div className="bg-white border-b border-gray-200" data-id="element-4358">
          {/* Cover Image */}
          <div className="h-48 md:h-64 bg-gradient-to-r from-primary-600 to-secondary-600 w-full relative" data-id="element-4359">
            <div className="absolute inset-0 bg-black/10" data-id="element-4360"></div>
          </div>

          <div className="container mx-auto px-4 md:px-6 relative" data-id="element-4361">
            <div className="flex flex-col md:flex-row items-start gap-6 -mt-16 mb-8" data-id="element-4362">
              {/* Avatar */}
              <div className="h-32 w-32 rounded-full bg-white p-1 shadow-lg flex-shrink-0 z-10" data-id="element-4363">
                <div className="h-full w-full rounded-full bg-primary-100 flex items-center justify-center text-primary-600 overflow-hidden" data-id="element-4364">
                  <Users className="h-16 w-16" data-id="element-4365" />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 pt-2 md:pt-16" data-id="element-4366">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-4367">
                  <div data-id="element-4368">
                    <div className="flex items-center gap-2 mb-1" data-id="element-4369">
                      <h1 className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-4370">
                        {communityData.name}
                      </h1>
                      {communityData.verified && <CheckCircle className="h-6 w-6 text-blue-500 fill-blue-500" data-id="element-4371" />}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-gray-600 text-sm" data-id="element-4372">
                      <span className="flex items-center gap-1" data-id="element-4373">
                        <MapPin className="h-4 w-4" data-id="element-4374" /> {communityData.location}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-4375">
                        <Calendar className="h-4 w-4" data-id="element-4376" /> Est.{' '}
                        {communityData.established}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-4377">
                        <Users className="h-4 w-4" data-id="element-4378" /> {communityData.members}{' '}
                        Anggota
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-3" data-id="element-4379">
                    <Button variant="outline" data-id="element-4380">Hubungi</Button>
                    <Button className="bg-primary-600 hover:bg-primary-700" data-id="element-4381">
                      Gabung Komunitas
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-8 border-b border-gray-100 overflow-x-auto" data-id="element-4382">
              {(['profil', 'kegiatan', 'galeri', 'kontak'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-4383">
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>)}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="container mx-auto px-4 md:px-6 py-8" data-id="element-4384">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-4385">
            {/* Left Column (Main) */}
            <div className="lg:col-span-2 space-y-8" data-id="element-4386">
              {activeTab === 'profil' && <div className="space-y-8 animate-in fade-in" data-id="element-4387">
                  <section data-id="element-4388">
                    <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-4389">
                      Tentang Komunitas
                    </h3>
                    <p className="text-gray-600 leading-relaxed" data-id="element-4390">
                      {communityData.description} Berawal dari keprihatinan
                      terhadap kondisi sampah di kota Surabaya, kami bergerak
                      bersama warga untuk menciptakan lingkungan yang lebih
                      bersih dan sehat. Kami percaya bahwa perubahan besar
                      dimulai dari langkah kecil di rumah tangga.
                    </p>
                  </section>

                  <section data-id="element-4391">
                    <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-4392">
                      Fokus Area
                    </h3>
                    <div className="flex flex-wrap gap-2" data-id="element-4393">
                      {communityData.focus.map(tag => <Badge key={tag} variant="secondary" className="px-3 py-1 text-sm" data-id="element-4394">
                          {tag}
                        </Badge>)}
                    </div>
                  </section>

                  <section data-id="element-4395">
                    <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-4396">
                      Visi & Misi
                    </h3>
                    <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm" data-id="element-4397">
                      <h4 className="font-semibold text-primary-700 mb-2" data-id="element-4398">
                        Visi
                      </h4>
                      <p className="text-gray-600 mb-4" data-id="element-4399">
                        Mewujudkan Surabaya sebagai kota Zero Waste pada tahun
                        2030.
                      </p>
                      <h4 className="font-semibold text-primary-700 mb-2" data-id="element-4400">
                        Misi
                      </h4>
                      <ul className="list-disc pl-5 space-y-1 text-gray-600" data-id="element-4401">
                        <li data-id="element-4402">Mengedukasi masyarakat tentang pemilahan sampah</li>
                        <li data-id="element-4403">
                          Mendorong kebijakan pengurangan plastik sekali pakai
                        </li>
                        <li data-id="element-4404">Membangun bank sampah di setiap RW</li>
                      </ul>
                    </div>
                  </section>
                </div>}

              {activeTab === 'kegiatan' && <div className="space-y-4 animate-in fade-in" data-id="element-4405">
                  {[1, 2, 3].map(i => <Card key={i} className="hover:shadow-md transition-shadow" data-id="element-4406">
                      <CardContent className="p-6" data-id="element-4407">
                        <div className="flex gap-4" data-id="element-4408">
                          <div className="h-16 w-16 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0 text-primary-600 font-bold" data-id="element-4409">
                            2{i} Feb
                          </div>
                          <div data-id="element-4410">
                            <h4 className="font-bold text-gray-900 text-lg mb-1" data-id="element-4411">
                              Workshop Kompos Rumahan Batch #{i}
                            </h4>
                            <p className="text-gray-600 mb-2" data-id="element-4412">
                              Pelatihan pembuatan kompos metode Takakura untuk
                              ibu rumah tangga.
                            </p>
                            <div className="flex items-center gap-4 text-sm text-gray-500" data-id="element-4413">
                              <span className="flex items-center gap-1" data-id="element-4414">
                                <MapPin className="h-3 w-3" data-id="element-4415" /> Balai RW 05
                              </span>
                              <span className="flex items-center gap-1" data-id="element-4416">
                                <Users className="h-3 w-3" data-id="element-4417" /> 30 Peserta
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>)}
                </div>}

              {activeTab === 'galeri' && <div className="grid grid-cols-2 md:grid-cols-3 gap-4 animate-in fade-in" data-id="element-4418">
                  {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="aspect-square bg-gray-200 rounded-lg overflow-hidden relative group" data-id="element-4419">
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-100" data-id="element-4420">
                        <ImageIcon className="h-8 w-8 text-gray-400" data-id="element-4421" />
                      </div>
                    </div>)}
                </div>}

              {activeTab === 'kontak' && <div className="space-y-6 animate-in fade-in" data-id="element-4422">
                  <Card data-id="element-4423">
                    <CardContent className="p-6 space-y-4" data-id="element-4424">
                      <h3 className="font-bold text-gray-900 mb-4" data-id="element-4425">
                        Informasi Kontak
                      </h3>
                      <div className="flex items-center gap-3 text-gray-600" data-id="element-4426">
                        <Mail className="h-5 w-5 text-gray-400" data-id="element-4427" />
                        <span data-id="element-4428">halo@nolsampah-sby.org</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600" data-id="element-4429">
                        <Phone className="h-5 w-5 text-gray-400" data-id="element-4430" />
                        <span data-id="element-4431">+62 812-3456-7890</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600" data-id="element-4432">
                        <Globe className="h-5 w-5 text-gray-400" data-id="element-4433" />
                        <span data-id="element-4434">www.nolsampah-sby.org</span>
                      </div>
                      <div className="pt-4 flex gap-4" data-id="element-4435">
                        <Button variant="outline" size="icon" className="rounded-full" data-id="element-4436">
                          <Instagram className="h-5 w-5" data-id="element-4437" />
                        </Button>
                        <Button variant="outline" size="icon" className="rounded-full" data-id="element-4438">
                          <Facebook className="h-5 w-5" data-id="element-4439" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card data-id="element-4440">
                    <CardContent className="p-6" data-id="element-4441">
                      <h3 className="font-bold text-gray-900 mb-4" data-id="element-4442">
                        Kirim Pesan
                      </h3>
                      <form className="space-y-4" data-id="element-4443">
                        <div className="grid grid-cols-2 gap-4" data-id="element-4444">
                          <Input placeholder="Nama Anda" data-id="element-4445" />
                          <Input placeholder="Email" type="email" data-id="element-4446" />
                        </div>
                        <Input placeholder="Subjek" data-id="element-4447" />
                        <Textarea placeholder="Pesan Anda..." className="min-h-[100px]" data-id="element-4448" />
                        <Button className="w-full bg-primary-600" data-id="element-4449">
                          Kirim Pesan
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                </div>}
            </div>

            {/* Right Column (Stats) */}
            <div className="lg:col-span-1 space-y-6" data-id="element-4450">
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-4451">
                <h3 className="font-bold text-gray-900 mb-4" data-id="element-4452">Dampak Sosial</h3>
                <div className="space-y-6" data-id="element-4453">
                  <div data-id="element-4454">
                    <p className="text-sm text-gray-500 mb-1" data-id="element-4455">Total Aksi</p>
                    <p className="text-2xl font-bold text-primary-600" data-id="element-4456">
                      {communityData.stats.actions}
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-4457">Kegiatan terlaksana</p>
                  </div>
                  <div className="h-px bg-gray-100" data-id="element-4458"></div>
                  <div data-id="element-4459">
                    <p className="text-sm text-gray-500 mb-1" data-id="element-4460">Partisipan</p>
                    <p className="text-2xl font-bold text-primary-600" data-id="element-4461">
                      {communityData.stats.participants}
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-4462">Orang terlibat</p>
                  </div>
                  <div className="h-px bg-gray-100" data-id="element-4463"></div>
                  <div data-id="element-4464">
                    <p className="text-sm text-gray-500 mb-1" data-id="element-4465">
                      Sampah Terkelola
                    </p>
                    <p className="text-2xl font-bold text-primary-600" data-id="element-4466">
                      {communityData.stats.tonnage} Ton
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-4467">Tidak masuk TPA</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-xl p-6 border border-blue-100" data-id="element-4468">
                <h3 className="font-bold text-blue-900 mb-2" data-id="element-4469">
                  Dukung Komunitas Ini
                </h3>
                <p className="text-sm text-blue-700 mb-4" data-id="element-4470">
                  Bantu mereka memperluas dampak dengan menjadi donatur atau
                  relawan tetap.
                </p>
                <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-id="element-4471">
                  <Heart className="h-4 w-4 mr-2" data-id="element-4472" /> Dukung Sekarang
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-4473" />
    </div>;
}